import axios from 'axios'
import { Message } from 'element-ui'
import router from '@/router'

const isDevelopment = process.env.NODE_ENV === 'development'
const baseURL = process.env.VUE_APP_API_BASE_URL || (isDevelopment ? 'http://localhost:8080' : '/api')

const service = axios.create({
  baseURL,
  timeout: 10000
})

// 过滤请求参数中为空字符串或 undefined 的字段
function cleanParams(obj) {
  if (!obj || Object.prototype.toString.call(obj) !== '[object Object]') {
    return obj
  }
  const cleaned = {}
  Object.keys(obj).forEach(key => {
    const value = obj[key]
    // 跳过空字符串、undefined、null
    if (value === '' || value === undefined || value === null) {
      return
    }
    // 可选：跳过空数组
    if (Array.isArray(value) && value.length === 0) {
      return
    }
    cleaned[key] = value
  })
  return cleaned
}

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 清理 query 参数和 JSON body 中的空值字段
    if (config.params) {
      config.params = cleanParams(config.params)
    }
    // 仅在普通对象时清理 body，避免破坏 FormData、字符串等
    if (config.data && Object.prototype.toString.call(config.data) === '[object Object]') {
      config.data = cleanParams(config.data)
    }

    const accessToken = localStorage.getItem('accessToken')
    // 登录接口不需要token
    const isLoginUrl =
      config.url === '/login' ||
      config.url === '/api/login' ||
      config.url === '/admin/auth/login' ||
      config.url === '/api/admin/auth/login' ||
      (typeof config.url === 'string' && config.url.endsWith('/admin/auth/login'))

    if (!isLoginUrl && !accessToken) {
      // 如果没有accessToken，跳转到登录页（避免重复导航）
      if (router.currentRoute.path !== '/login') {
        router.push('/login').catch(err => {
          // 忽略重复导航错误
          if (err.name !== 'NavigationDuplicated') {
            console.error(err)
          }
        })
      }
    }
    // 如果有accessToken，添加到请求头
    if (accessToken && !isLoginUrl) {
      config.headers['Authorization'] = 'Bearer ' + accessToken
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    // 判断HTTP状态码是否为200
    if (response.status === 200) {
      const res = response.data
      const url = response.config && response.config.url

      // system-config 等特殊接口兼容：
      // 后端可能直接返回数组，或者 data 为数组
      if (url && typeof url === 'string' && url.indexOf('/admin/system-config') !== -1) {
        // 1) 直接返回数组
        if (Array.isArray(res)) return res
        // 2) 标准包装 { code, message, data: [...] }
        if (res && typeof res === 'object' && 'code' in res) {
          if (res.code === 0) return res.data
          if (res.code === 40100) {
            localStorage.removeItem('accessToken')
            if (router.currentRoute.path !== '/login') {
              router.push('/login').catch(err => {
                if (err.name !== 'NavigationDuplicated') {
                  console.error(err)
                }
              })
            }
            Message.error('登录已过期，请重新登录')
          } else {
            Message.error(res.message || '请求失败')
          }
          return Promise.reject(res)
        }
        // 3) 兼容 { data: [...] }
        if (res && Array.isArray(res.data)) return res.data
      }

      // system-config 批量更新接口比较特殊：可能无统一的 code/data 包装
      // 只要接口返回 200 即认为成功，不做 code 判断，直接返回原始 res
      if (url && typeof url === 'string' && url.indexOf('/admin/system-config/batch-update') !== -1) {
        // 如果有标准包装，仍按 code===0 才成功
        if (res && typeof res === 'object' && 'code' in res) {
          if (res.code === 0) return res.data
          if (res.code === 40100) {
            localStorage.removeItem('accessToken')
            if (router.currentRoute.path !== '/login') {
              router.push('/login').catch(err => {
                if (err.name !== 'NavigationDuplicated') {
                  console.error(err)
                }
              })
            }
            Message.error('登录已过期，请重新登录')
          } else {
            Message.error(res.message || '请求失败')
          }
          return Promise.reject(res)
        }
        // 无任何返回体也视为成功（进入 then）
        return res
      }

      // 判断response的json中code是否为0
      if (res && res.code === 0) {
        // 成功：返回 data
        return res.data
      } else if (res && res.code === 40100) {
        localStorage.removeItem('accessToken')
        if (router.currentRoute.path !== '/login') {
          router.push('/login').catch(err => {
            if (err.name !== 'NavigationDuplicated') {
              console.error(err)
            }
          })
        }
        Message.error('登录已过期，请重新登录')
        return Promise.reject(res)
      } else {
        // 不为0则消息框弹出response.message中的内容
        Message.error((res && res.message) || '请求失败')
        return Promise.reject(res)
      }
    }else if(response.status === 401 || response.status === 403) {
      localStorage.removeItem('accessToken')
      if (router.currentRoute.path !== '/login') {
        router.push('/login').catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error(err)
          }
        })
      }
      Message.error('登录已过期，请重新登录')
      return Promise.reject(response)
    }
    return response
  },
  error => {
    const isLoginUrl =
      typeof error.config?.url === 'string' &&
      error.config.url.endsWith('/admin/auth/login')

    // 登录失败不应被当成已有会话过期，也不应打断登录表单的错误提示。
    if (error.response && (error.response.status === 401||error.response.status === 403)) {
      if (isLoginUrl) {
        return Promise.reject(error)
      }
      localStorage.removeItem('accessToken')
      if (router.currentRoute.path !== '/login') {
        router.push('/login').catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error(err)
          }
        })
      }
      if (router.currentRoute.path !== '/login') {
        Message.error('登录已过期，请重新登录')
      }
      return Promise.reject(error)
    }
    Message.error(error.message || '网络错误')
    return Promise.reject(error)
  }
)

export default service
